import { useState, useEffect } from 'react';
import type { QuestionnaireQuestion } from '../api/types';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/utils/styling';

type Props = {
  question: QuestionnaireQuestion;
  setQuestionAnswer: (questionId: string, answerIds: string[]) => void;
  selectedAnswers?: string[];
  hasValidationError?: boolean;
};

const VerificationQuestion = ({
  question,
  setQuestionAnswer,
  selectedAnswers = [],
  hasValidationError = false,
}: Props) => {
  const { t } = useTranslation();
  const [localSelectedAnswers, setLocalSelectedAnswers] =
    useState<string[]>(selectedAnswers);

  useEffect(() => {
    setLocalSelectedAnswers(selectedAnswers);
  }, [selectedAnswers]);

  const handleSingleChoiceChange = (answerId: string) => {
    const newAnswers = [answerId];
    setLocalSelectedAnswers(newAnswers);
    setQuestionAnswer(question.questionId, newAnswers);
  };

  const handleMultipleChoiceChange = (answerId: string, checked: boolean) => {
    let newAnswers: string[];
    if (checked) {
      newAnswers = [...localSelectedAnswers, answerId];
    } else {
      newAnswers = localSelectedAnswers.filter(id => id !== answerId);
    }
    setLocalSelectedAnswers(newAnswers);
    setQuestionAnswer(question.questionId, newAnswers);
  };

  const isSingleChoice = question.answers.some(
    answer => answer.answerTypeCode?.toLowerCase() === 'single'
  );
  const isMultipleChoice = question.answers.some(
    answer => answer.answerTypeCode?.toLowerCase() === 'multiple'
  );

  return (
    <div>
      <h4 className="headline4 font-medium text-primary mb-[1.6rem] pt-[0.8rem]">
        {question.questionValue}
      </h4>

      {isSingleChoice ? (
        <RadioGroup
          value={localSelectedAnswers[0] || ''}
          onValueChange={handleSingleChoiceChange}
          className="flex flex-col gap-[0.8rem]">
          {question.answers.map(answer => (
            <div
              key={answer.answerId}
              className="flex items-start gap-[1.6rem] py-[0.8rem] cursor-pointer">
              <RadioGroupItem
                value={answer.answerId}
                id={`${question.questionId}-${answer.answerId}`}
                className={cn(
                  'border-none',
                  hasValidationError && 'bg-light-error'
                )}
              />
              <Label
                htmlFor={`${question.questionId}-${answer.answerId}`}
                className={cn(
                  'py-[0.2rem] p4 cursor-pointer flex-1',
                  hasValidationError && 'text-destructive'
                )}>
                {answer.answerValue}
              </Label>
            </div>
          ))}
        </RadioGroup>
      ) : isMultipleChoice ? (
        <div className="space-y-3">
          {question.answers.map(answer => (
            <div
              key={answer.answerId}
              className="flex items-baseline gap-[1.2rem]">
              <Checkbox
                id={`${question.questionId}-${answer.answerId}`}
                checked={localSelectedAnswers.includes(answer.answerId)}
                onCheckedChange={checked =>
                  handleMultipleChoiceChange(
                    answer.answerId,
                    checked as boolean
                  )
                }
                className={cn(hasValidationError && 'bg-light-error')}
              />
              <Label
                htmlFor={`${question.questionId}-${answer.answerId}`}
                className={cn(
                  'p5 leading-none cursor-pointer',
                  hasValidationError && 'bg-light-error'
                )}>
                {answer.answerValue}
              </Label>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500">
          {t('personalAccount.verification.questionaries.noAnswersAvailable')}
        </div>
      )}
    </div>
  );
};

export { VerificationQuestion };
